import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn, formatNumber } from '@/lib/utils'
import { LANGUAGES } from '@/types/terms'
import type { SortKey } from '@/hooks/use-term-data'
import { DocumentFilterSection, type DocumentFilter } from './document-filter-section'

interface FiltersPanelProps {
  lang: string
  domain: string
  abbrOnly: boolean
  sort: SortKey
  domains: string[]
  shown: number
  total: number
  /** Selected documents narrowing this list; null when none. */
  documentFilter?: DocumentFilter | null
  onChange: (patch: {
    lang?: string
    domain?: string
    abbrOnly?: boolean
  }) => void
  onSortChange: (s: SortKey) => void
  onClear: () => void
}

export function FiltersPanel({
  lang,
  domain,
  abbrOnly,
  sort,
  domains,
  shown,
  total,
  documentFilter,
  onChange,
  onSortChange,
  onClear,
}: FiltersPanelProps) {
  const { t } = useTranslation()
  const [domainFilter, setDomainFilter] = useState('')

  // 200+ domains is too many for a plain <select>; make it searchable.
  const visibleDomains = useMemo(() => {
    const f = domainFilter.trim().toLowerCase()
    const list = f ? domains.filter((d) => d.toLowerCase().includes(f)) : domains
    return list.slice(0, 200)
  }, [domains, domainFilter])

  const hasFilters = !!(lang || domain || abbrOnly)

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto scrollbar-thin p-3">
      <DocumentFilterSection filter={documentFilter} />
      <div>
        <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('filters.language')}
        </div>
        <ToggleGroup
          type="single"
          value={lang || 'all'}
          onValueChange={(v) => onChange({ lang: !v || v === 'all' ? '' : v })}
          className="w-full"
        >
          <ToggleGroupItem value="all" className="flex-1">
            {t('common.all')}
          </ToggleGroupItem>
          {LANGUAGES.map((l) => (
            <ToggleGroupItem key={l} value={l} className="flex-1 uppercase">
              {l}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div>
        <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('common.sortBy')}
        </div>
        <ToggleGroup
          type="single"
          value={sort}
          onValueChange={(v) => v && onSortChange(v as SortKey)}
          className="w-full"
        >
          <ToggleGroupItem value="freq" className="flex-1">
            {t('filters.sortFreq')}
          </ToggleGroupItem>
          <ToggleGroupItem value="docs" className="flex-1">
            {t('filters.sortDocs')}
          </ToggleGroupItem>
          <ToggleGroupItem value="alpha" className="flex-1">
            {t('common.alphabetical')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-xs">
          <Checkbox
            checked={abbrOnly}
            onCheckedChange={(v) => onChange({ abbrOnly: v === true })}
          />
          {t('filters.abbreviationsOnly')}
        </label>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('filters.domain')}
          </span>
          {domain && (
            <Button variant="ghost" size="icon-sm" onClick={() => onChange({ domain: '' })}>
              <X />
            </Button>
          )}
        </div>
        <div className="relative mb-1.5">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            placeholder={t('filters.searchDomain')}
            className="h-7 pl-7 text-xs"
          />
        </div>
        <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
          {visibleDomains.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onChange({ domain: d === domain ? '' : d })}
              className={cn(
                'block w-full truncate px-2 py-1 text-left text-[11px] transition-colors',
                d === domain
                  ? 'bg-accent font-medium text-accent-foreground'
                  : 'hover:bg-muted',
              )}
              title={d}
            >
              {d}
            </button>
          ))}
          {!visibleDomains.length && (
            <p className="p-2 text-[11px] text-muted-foreground">{t('common.noMatches')}</p>
          )}
        </div>
      </div>

      <div className="shrink-0 space-y-2 border-t border-border pt-2">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>
            <Badge variant="primary">{formatNumber(shown)}</Badge> / {formatNumber(total)}
          </span>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={onClear}>
              {t('common.clear')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
